import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { scheduleKey, committedActivitiesKey, WEATHER_DAY_KEY } from '@constants/queryKeys';
import { useToast } from '@components/ui/Toasts/toastContext';
import type { IRootState } from '@store/slices';
import type { ISchedulerData, SiteId, TaskId } from '@interfaces/IScheduler';
import { withTaskExecution } from '../lib/taskViewCodec';
import { writeRefusalCopy } from '../lib/writeRefusalCopy';
import { useRestoreServerState } from './useRestoreServerState';
import {
  flushImmediate,
  immediateWriteKey,
  writeImmediate,
  type IImmediateWriteOptions,
} from '../lib/immediateWriteChannel';

/** The (site, schedule, task) a granular write resolves against — handed to `send`. */
export interface IImmediateWriteContext {
  siteId: SiteId;
  scheduleNumber: string;
  taskId: TaskId;
}

export interface IImmediateWritePersist {
  /**
   * Queue `value` on the channel identified by `keyParts` (a field kind, then
   * any per-item disambiguator, e.g. `['check', itemId]` or `['progress']`).
   * `send` performs the actual PUT/POST; on success the cached schedule takes
   * the task's execution from the store and the activities and weather-day
   * queries are invalidated. When the channel gives the value
   * up (retries exhausted, or refused outright) the page is put back to what
   * the server holds and ONE error toast says so. No-op
   * when `scheduleNumber` is absent (Router-free tests) — the caller's store
   * update already happened optimistically before calling `write`.
   */
  write<T>(
    keyParts: readonly string[],
    value: T,
    send: (value: T, ctx: IImmediateWriteContext) => Promise<unknown>,
    options?: IImmediateWriteOptions<T>,
  ): void;
  /** Whether writes are live (i.e. `scheduleNumber` is present). */
  readonly enabled: boolean;
}

/**
 * Shared plumbing behind every per-task immediate-write hook (checklist,
 * comments, progress, locations): the queryClient wiring, the cache update and
 * the `committedActivitiesKey` + `WEATHER_DAY_KEY` invalidate on a successful
 * send, the rollback and its toast when a write is given up, and
 * flush-on-unmount / task-switch for every channel key touched during the
 * surface's lifetime.
 *
 * A give-up is FINAL: the whole-doc Save never writes these fields for an
 * existing task, so there is nothing left to recover the value — leaving it
 * on screen would show a change that never happened. The page re-reads the
 * server (`useRestoreServerState`) and the toast says the change was undone,
 * or, when even that read fails, asks for a reload — unless the server refused
 * the value on purpose, when it says why (`writeRefusalCopy`). The toast comes from the
 * shared queue, so every host of these surfaces reports it.
 * Each per-field hook becomes a thin wrapper that only knows its own key shape
 * and API call — the channel semantics (debounce, coalesce, retry, teardown)
 * live here, once.
 */
export function useImmediateWritePersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
): IImmediateWritePersist {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pushToast = useToast();
  const restore = useRestoreServerState(siteId);
  // Keys touched this session, so the flush effect can drain per-item channels
  // (their keys are dynamic — one per itemId/comment id/locationId) on unmount
  // / task switch.
  const keysRef = useRef<Set<string>>(new Set());

  const store = useStore<IRootState>();

  const settle = useCallback(() => {
    // The cached schedule is the page's SAVED BASELINE — what the Save review,
    // the dirty check and the stale-copy rebase compare the working copy with —
    // so it may only move with the store: a load, a Save, a rebase, our own
    // acknowledged plan write. Re-reading it here would pull in another
    // editor's plan changes the store doesn't have, and the next Save would
    // write the old values back over them (QA F-90). The write only changed
    // this task's execution, so the cache takes exactly that, from the store
    // (plan writes settle their half in `useOwnPlanWriteAck`).
    qc.setQueryData<ISchedulerData>(scheduleKey(siteId, scheduleNumber), (cached) =>
      cached && withTaskExecution(cached, taskId, store.getState().scheduler.activityBySite[siteId]?.[taskId]),
    );
    void qc.invalidateQueries({ queryKey: committedActivitiesKey(siteId) });
    // Progress/status writes change whether a task counts as weather-active —
    // refresh the dashboard weather card. The backend insights cache keys on
    // the per-day activity set, so a no-op refetch costs no AI call.
    void qc.invalidateQueries({ queryKey: WEATHER_DAY_KEY });
  }, [qc, store, siteId, scheduleNumber, taskId]);

  const giveUp = useCallback(
    async (onSchedule: string, error: unknown) => {
      const restored = await restore(onSchedule);
      const copy = writeRefusalCopy(error) ?? {
        title: 'scheduler.writeFailed.title',
        sub: restored ? 'scheduler.writeFailed.undone' : 'scheduler.writeFailed.reload',
      };
      pushToast({ kind: 'error', title: t(copy.title), sub: t(copy.sub) });
    },
    [restore, pushToast, t],
  );

  const write = useCallback(
    <T>(
      keyParts: readonly string[],
      value: T,
      send: (value: T, ctx: IImmediateWriteContext) => Promise<unknown>,
      options?: IImmediateWriteOptions<T>,
    ) => {
      if (!scheduleNumber) return; // no route context (tests) → optimistic store only
      const [kind, ...rest] = keyParts;
      const key = immediateWriteKey(kind!, siteId, scheduleNumber, taskId, ...rest);
      keysRef.current.add(key);
      const ctx: IImmediateWriteContext = { siteId, scheduleNumber, taskId };
      writeImmediate<T>(
        key,
        value,
        (v) => send(v, ctx).then(settle),
        (error) => void giveUp(scheduleNumber, error),
        options,
      );
    },
    [giveUp, settle, siteId, scheduleNumber, taskId],
  );

  useEffect(() => {
    const keys = keysRef.current;
    return () => {
      for (const key of keys) flushImmediate(key);
      keys.clear();
    };
  }, [siteId, scheduleNumber, taskId]);

  return useMemo(() => ({ write, enabled: scheduleNumber !== undefined }), [write, scheduleNumber]);
}
